import { availableFields } from '../../../shared/api';
import type { CardField, FieldMode, FlashcardConfig, Language } from '../../../shared/types';

/**
 * What to call a card field in this language.
 *
 * The romanization is named for what it is — "Pinyin", "Rōmaji" — rather than
 * "Romanization", because the former reads as a thing and the latter as a
 * setting. The written form takes the language's own name for the same reason.
 */
export function fieldName(field: CardField, language: Pick<Language, 'name' | 'transliteration'>): string {
  switch (field) {
    case 'target':
      return language.name;
    case 'translit':
      return language.transliteration?.name ?? 'Romanization';
    case 'translation':
      return 'English';
    case 'audio':
      return 'audio';
  }
}

/**
 * The card setup in one sentence: "Front: audio. Back: Spanish, English."
 *
 * Shown wherever a drill can be started, so nobody has to open the panel to
 * remember what they are about to be asked.
 */
export function describeSetup(
  config: FlashcardConfig,
  language: Pick<Language, 'name' | 'transliteration'>,
): string {
  const fields = availableFields(language);
  const side = (mode: FieldMode) =>
    fields.filter((field) => config.fields[field] === mode).map((field) => fieldName(field, language));

  const front = side('prompt');
  const back = side('answer');
  const off = side('hidden');

  const parts = [
    front.length ? `Front: ${front.join(', ')}.` : 'Front: nothing.',
    back.length ? `Back: ${back.join(', ')}.` : 'Back: nothing.',
  ];
  if (off.length) parts.push(`Hidden: ${off.join(', ')}.`);
  return parts.join(' ');
}
